import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import Stack from '@mui/material/Stack';
import omit from 'lodash/omit';
import startCase from 'lodash/startCase';
import toLower from 'lodash/toLower';
import moment from 'moment-timezone';
import qs from 'qs';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ICalendarEventsParams } from 'src/api/dtos/calendarEvents.dto';
import CalendarRepository from 'src/api/repositories/ess/v1/calendar.repository';
import { EmployeeAvailabilityButton } from 'src/features/availabilities';
import { CalendarEventCategoryType } from 'src/features/calendar/calendar.types';
import { DateRangeSelector, ExportPdfIcon, Tooltip } from 'src/shared/components';
import { FULL_MONTH_YEAR_FORMAT, MONTH_YEAR_FORMAT, PDF_HIDDEN_CLASS } from 'src/shared/constants';
import { ComponentWithPdfExport } from 'src/shared/hocs';
import { useQuery } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { DateRangeType } from 'src/shared/types';
import { getDateRangeFromMonth, isDateWithinDateRange, parseQuery } from 'src/shared/utils';
import { RootState } from 'src/store/types';

import { MonthlyAbsenceRequest } from './MonthlyAbsenceRequest';
import { MonthlyCalendarEvents } from './MonthlyCalendarEvents';
import { MonthlyCalendarFilters } from './MonthlyCalendarFilters';

const PDF_OPTIONS = { orientation: 'landscape', format: 'a2' };

type QueryParamsTypes = { month: string } & Pick<
  ICalendarEventsParams,
  | 'includeApprovedAbsences'
  | 'includePendingAbsences'
  | 'includeRejectedAbsences'
  | 'includePartialDayAbsences'
  | 'includeSchedules'
  | 'includeDepartmentAbsences'
  | 'includeAvailabilities'
  | 'includeShiftOffers'
  | 'includeDepartmentSchedules'
>;

export const CalendarPage = memo(() => {
  const { t } = useTranslate();
  const query = useQuery();
  const navigate = useNavigate();
  const calendarRef = useRef<HTMLDivElement | null>(null);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState<boolean>(false);
  const { settings, info } = useSelector((state: RootState) => state.user);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEventCategoryType | undefined>();

  const queryParams: QueryParamsTypes = useMemo(() => {
    const q = query ? parseQuery<QueryParamsTypes>(query.toString()) : {};
    return { ...getDefaultQueryParams(), ...q };
  }, [query]);

  const dateRange = useMemo(
    () => ({
      startDate: moment(queryParams.month, MONTH_YEAR_FORMAT).format(),
      endDate: moment(queryParams.month, MONTH_YEAR_FORMAT).format(),
    }),
    [queryParams],
  );

  const formattedMonth = moment(queryParams.month, MONTH_YEAR_FORMAT).format(FULL_MONTH_YEAR_FORMAT);
  const calendarPdfFileName = useMemo(() => `${t('calendar.calendar')}-${formattedMonth}`, [queryParams.month]);

  const newDateRange = getDateRangeFromMonth(queryParams.month);
  const params = { from: newDateRange.startDate, to: newDateRange.endDate, ...omit(queryParams, ['month']) };
  const { data: events, isLoading, isFetching } = CalendarRepository.endpoints.useGetEventsQuery(params);

  const loading = isLoading || isFetching;

  const formatter = useCallback((dateRange: DateRangeType) => {
    const today = moment();
    const { startDate, endDate } = dateRange;
    const momentStartDate = moment(startDate);
    const momentEndDate = moment(endDate);

    const date = momentEndDate.format(FULL_MONTH_YEAR_FORMAT);

    if (
      isDateWithinDateRange(today, {
        startDate: momentStartDate.startOf('month').format(),
        endDate: momentEndDate.endOf('month').format(),
      })
    ) {
      return `${startCase(toLower(t('global.currentMonth')))}: ${date}`;
    }

    const lastMonth = today.clone().subtract(1, 'month');

    if (
      isDateWithinDateRange(lastMonth, {
        startDate: momentStartDate.startOf('month').format(),
        endDate: momentEndDate.endOf('month').format(),
      })
    ) {
      return `${startCase(toLower(t('global.lastMonth')))}: ${date}`;
    }

    const nextMonth = today.clone().add(1, 'month');

    if (
      isDateWithinDateRange(nextMonth, {
        startDate: momentStartDate.startOf('month').format(),
        endDate: momentEndDate.endOf('month').format(),
      })
    ) {
      return `${startCase(toLower(t('global.nextMonth')))}: ${date}`;
    }

    return date;
  }, []);

  const handleDateRangeChange = useCallback(
    ({ endDate }: DateRangeType) => {
      const month = moment(endDate).format(MONTH_YEAR_FORMAT);

      const search = qs.stringify({ ...queryParams, month }, { arrayFormat: 'brackets' });
      navigate({ search });
    },
    [history, queryParams],
  );

  const handleFiltersChange = useCallback(
    (filters: Omit<QueryParamsTypes, 'month'>) => {
      const search = qs.stringify({ ...queryParams, ...filters }, { arrayFormat: 'brackets' });
      navigate({ search });
    },
    [queryParams],
  );

  return (
    <div id="page-wrapper">
      <h2>{t('calendar.calendar')}</h2>
      <div className="mt-4 d-flex justify-content-between" ref={calendarRef}>
        <MonthlyCalendarFilters
          disabled={loading}
          filters={omit(queryParams, ['month'])}
          onFilter={handleFiltersChange}
          isPartialAbsenceEnabled={!!settings?.isPartialAbsenceEnabled}
        />
        <div className="flex-grow-1">
          <div className={`d-flex align-items-center justify-content-between ${PDF_HIDDEN_CLASS}`}>
            <DateRangeSelector
              type="monthly"
              dateRange={dateRange}
              formatter={formatter}
              onDateRangeChange={handleDateRangeChange}
            />
            <div className="d-flex align-items-center justify-content-between">
              <Tooltip title={t('calendar.exportToPdf')}>
                <div>
                  <ComponentWithPdfExport
                    targetRef={calendarRef}
                    filename={calendarPdfFileName}
                    options={PDF_OPTIONS}
                    onLoad={setIsDownloadingPdf}
                    header={
                      <div className="mt-4">
                        <h6 className="text-center">{t('calendar.calendar')}</h6>
                        <h4 className="text-center text-muted fw-400">{formattedMonth}</h4>
                      </div>
                    }
                  >
                    {(exportPDF) => (
                      <LoadingButton
                        className="w-unset px-2 me-2"
                        variant="contained"
                        color="secondary"
                        loading={isDownloadingPdf}
                        onClick={exportPDF}
                      >
                        <ExportPdfIcon />
                      </LoadingButton>
                    )}
                  </ComponentWithPdfExport>
                </div>
              </Tooltip>

              <Stack direction="row" alignItems="center" gap={1}>
                <EmployeeAvailabilityButton
                  defaultAvailability={selectedEvent?.type === 'availability' ? selectedEvent.value : undefined}
                  onResetAvailability={() => setSelectedEvent(undefined)}
                />
                <MonthlyAbsenceRequest />
              </Stack>
            </div>
          </div>
          <MonthlyCalendarEvents
            employeeId={info?.employeeId}
            month={queryParams.month}
            events={events}
            settings={settings}
            loading={loading}
            selectedEvent={selectedEvent}
            onSelectedEventChange={setSelectedEvent}
          />
        </div>
      </div>
    </div>
  );
});

const getDefaultQueryParams = (): QueryParamsTypes => {
  return {
    month: moment().startOf('month').format(MONTH_YEAR_FORMAT),
    includeApprovedAbsences: true,
    includePendingAbsences: true,
    includeRejectedAbsences: true,
    includePartialDayAbsences: true,
    includeSchedules: true,
    includeDepartmentAbsences: true,
    includeAvailabilities: true,
    includeShiftOffers: true,
    includeDepartmentSchedules: false,
  };
};
